/**
 * Renders the details-step CTA descriptor from usePaymentStepCta. One button,
 * two behaviours: a native form submit (relay) or an onClick action
 * (prepay / stripe / pick). Used by the desktop sidebar, the mobile
 * order-summary bar and the in-form content CTA so they never drift again.
 *
 * Relay (form submit): the sticky bars sit OUTSIDE the form, so they pass
 * `formId="booking-form"` to associate the submit. The in-form content button
 * omits it — a plain `type="submit"` submits its enclosing form, and adding the
 * attr would make it a second `[form="booking-form"]` match alongside the bar.
 */
import { Loader2 } from "lucide-react";
import type { PaymentStepCta } from "./usePaymentStepCta";

// Horizontal padding is intentionally omitted — callers pass it (px-4 in the
// sidebar / content, px-8 in the mobile bar) so there's no conflicting class.
const BASE_CLASS =
  "bg-aubergine-800 hover:bg-aubergine-800/90 text-white rounded-full h-[44px] font-medium text-base normal-case flex items-center justify-center gap-2 transition-colors disabled:opacity-50 disabled:pointer-events-none";

export const PaymentCtaButton = ({
  cta,
  className = "",
  submitTestId,
  formId,
}: {
  cta: PaymentStepCta;
  className?: string;
  /** data-testid for this instance of the CTA. Both variants carry it: the
   *  control is mounted up to four times at once (in-form content, desktop
   *  sidebar, order-summary panel, mobile bar) and its rendered label is the
   *  same translated string in every one, so without a per-instance id the
   *  recorder resolves it by position and refuses the recording (EAGNT-879). */
  submitTestId?: string;
  /** Associate the relay submit with a form by id — set by the out-of-form
   *  sticky bars; omit for the in-form content button. */
  formId?: string;
}) => {
  const content = cta.loading ? (
    <>
      <Loader2 className="h-4 w-4 animate-spin" />
      {cta.label}
    </>
  ) : (
    cta.label
  );

  if (cta.kind === "relay") {
    return (
      <button
        type="submit"
        form={formId}
        disabled={cta.disabled}
        aria-disabled={cta.disabled}
        data-testid={submitTestId}
        className={`${BASE_CLASS} ${className}`}
      >
        {content}
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={cta.onClick}
      disabled={cta.disabled}
      data-testid={submitTestId}
      className={`${BASE_CLASS} ${className}`}
    >
      {content}
    </button>
  );
};
